import { apiJson } from '@/api/client'
import type {
  AddNotePayload,
  AlertDetail,
  AlertFilters,
  AlertNote,
  AlertsListResponse,
  BulkAssignPayload,
  BulkClosePayload,
  BulkEscalatePayload,
  SavedFilter,
  SortKey,
} from './types'

export interface ListArgs {
  filters?: AlertFilters
  sort?: SortKey[]
  page?: number
  pageSize?: number
}

function buildSearchParams({ filters = {}, sort = [], page = 1, pageSize = 50 }: ListArgs) {
  const sp = new URLSearchParams()
  sp.set('page', String(page))
  sp.set('page_size', String(pageSize))
  for (const s of sort) sp.append('sort', `${String(s.field)}:${s.dir}`)
  for (const v of filters.status ?? []) sp.append('status', v)
  for (const v of filters.priority ?? []) sp.append('priority', v)
  for (const v of filters.scenario ?? []) sp.append('scenario', v)
  for (const v of filters.queue_id ?? []) sp.append('queue_id', v)
  for (const v of filters.channel ?? []) sp.append('channel', v)
  if (filters.assigned_to === 'unassigned') sp.append('assigned_to', 'unassigned')
  else for (const v of filters.assigned_to ?? []) sp.append('assigned_to', v)
  if (filters.sla_at_risk) sp.set('sla_at_risk', 'true')
  if (filters.sla_breached) sp.set('sla_breached', 'true')
  if (filters.amount_min_minor !== undefined)
    sp.set('amount_min_minor', String(filters.amount_min_minor))
  if (filters.amount_max_minor !== undefined)
    sp.set('amount_max_minor', String(filters.amount_max_minor))
  if (filters.date_from) sp.set('date_from', filters.date_from)
  if (filters.date_to) sp.set('date_to', filters.date_to)
  if (filters.q) sp.set('q', filters.q)
  return sp
}

// Wire-shape translator: case-service envelopes its list responses as
// `{items, total, ...}` (the platform-wide convention for paginated
// lists) but the React side has historically modeled this as
// `{alerts, total, ...}`. Translate at the adapter so Go and React
// can each keep their canonical shapes — flipping either would
// either break the rest of the platform's list endpoints (Go) or
// cascade through every alert-related component (React).
type AlertsListWire =
  | AlertsListResponse
  | (Omit<AlertsListResponse, 'alerts'> & { items: AlertsListResponse['alerts'] })

export async function listAlerts(args: ListArgs): Promise<AlertsListResponse> {
  const sp = buildSearchParams(args)
  const wire = await apiJson<AlertsListWire>(`/alerts?${sp.toString()}`)
  if ('alerts' in wire && Array.isArray(wire.alerts)) {
    return wire
  }
  if ('items' in wire && Array.isArray(wire.items)) {
    const { items, ...rest } = wire
    return { ...rest, alerts: items }
  }
  // Defensive fallback so a bare `{total: 0}` envelope (no array at
  // all) doesn't crash useAlertsInfinite's `p.alerts.length` reduce.
  const fallback: AlertsListResponse = {
    alerts: [],
    total: 0,
    page: args.page ?? 1,
    page_size: args.pageSize ?? 50,
    queue_depths: {},
  }
  return { ...fallback, ...(wire as Partial<AlertsListResponse>) }
}

export async function getAlert(id: string): Promise<AlertDetail> {
  const wire = await apiJson<AlertDetail & { customer?: Partial<AlertDetail['customer_snapshot']> }>(
    `/alerts/${id}`,
  )
  // Wire-shape resilience: the live backend currently returns a slim
  // `customer` object on the detail payload (id + display_name +
  // customer_number) instead of the rich `customer_snapshot` the UI
  // expects. When customer_snapshot is missing, synthesise a
  // best-effort shape from `customer` so the rail and detail page
  // can still render — falling values default to safe placeholders
  // (no risk score, unverified KYC, no PEP, no BVN). When the
  // backend enrichment lands the spread will simply pass through.
  if (!wire.customer_snapshot) {
    const c = wire.customer ?? ({} as Partial<AlertDetail['customer_snapshot']>)
    wire.customer_snapshot = {
      id: c.id ?? '',
      customer_number: c.customer_number ?? '',
      display_name: c.display_name ?? 'Unknown customer',
      risk_score: c.risk_score ?? null,
      risk_score_history: c.risk_score_history ?? [],
      kyc_tier: c.kyc_tier ?? 'unverified',
      pep: c.pep ?? false,
      bvn_masked: c.bvn_masked ?? null,
    }
  }
  return wire
}

export function listSavedFilters(): Promise<{ saved_filters: SavedFilter[] }> {
  return apiJson('/alerts/saved-filters')
}

export function bulkAssignAlerts(payload: BulkAssignPayload) {
  return apiJson<{ updated: number }>('/alerts/bulk/assign', { method: 'POST', body: payload })
}

export function bulkCloseAlerts(payload: BulkClosePayload) {
  return apiJson<{ updated: number }>('/alerts/bulk/close', { method: 'POST', body: payload })
}

export function bulkEscalateAlerts(payload: BulkEscalatePayload) {
  return apiJson<{ updated: number }>('/alerts/bulk/escalate', { method: 'POST', body: payload })
}

export function addAlertNote(payload: AddNotePayload) {
  return apiJson<{ note: AlertNote; alert_id: string }>(
    `/alerts/${payload.alert_id}/notes`,
    { method: 'POST', body: { body: payload.body } },
  )
}
